
import React, { useState, useEffect } from "react";
import "../styles/EducationPage.css";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import SEO from "../components/SEO";

import * as productService from "../_services/products.service";

export default function MagyneCampusPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState(location.state?.product || null);

  const params = new URLSearchParams(location.search);

  const proceedTo = params.get("proceedTo") || "register";
  const tenantId = params.get("tenantId") || null;

  const productId =
    params.get("productId") ||
    "555ae16a-8b65-4b4f-bd00-70de6c5a24ac";

  const isActive =
    product?.status?.toUpperCase() === "ACTIVE";

  const handleNavigate = () => {
    if (!product) {
      toast.error("Product information is unavailable.");
      return;
    }

    const status = product.status?.toUpperCase();

    if (status !== "ACTIVE") {
      if (status === "PLANNED") {
        toast("Magyne Campus is planned and will be available soon.", {
          icon: "🎓",
        });
      } else if (status === "DEVELOPMENT") {
        toast("Magyne Campus is Coming Soon.", {
          icon: "🛠️",
        });
      } else {
        toast.error("This product is currently unavailable.");
      }

      return;
    }

    navigate(
      `/pricing/?productId=${product.productId}&proceedTo=${proceedTo}&tenantId=${tenantId}`,
      {
        state: { product },
      }
    );
  };

  const getStatusMessage = () => {
    switch (product?.status?.toUpperCase()) {
      case "PLANNED":
        return "Magyne Campus is planned and will be available soon.";

      case "DEVELOPMENT":
        return "Magyne Campus is Coming Soon.";

      case "ACTIVE":
        return null;

      default:
        return "Magyne Campus is currently unavailable.";
    }
  };

  const getButtonText = () => {
    switch (product?.status?.toUpperCase()) {
      case "PLANNED":
        return "Coming Soon";

      case "DEVELOPMENT":
        return "Coming Soon";

      case "ACTIVE":
        return "Choose a Package";

      default:
        return "Currently Unavailable";
    }
  };

  useEffect(() => {
    const fetchProduct = async () => {
      if (!product && productId) {
        setLoading(true);

        try {
          const response = await productService.get({
            productId,
            status: "all"
          });

          if (!response.data?.length) {
            setProduct(null);
            return;
          }

          setProduct(response.data[0]);
        } catch (error) {
          toast.error(
            error?.message ||
              "Failed to fetch product details."
          );

          setProduct(null);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchProduct();
  }, [product, productId]);

  return (
    <>
      <SEO
        title="Magyne Campus | University & College Management Software"
        description="Magyne Campus is higher education management software for universities and colleges, helping manage students, admissions, academics, examinations, finance, staff and campus operations."
        path="/solutions/campus"
      />

      <div className="education-page">

        {/* Background */}
        <div className="bg-blobs">
          <div className="blob blob1"></div>
          <div className="blob blob2"></div>
          <div className="blob blob3"></div>
        </div>

        {loading ? (
          <div>
            <div className="spinner"></div>

            <p className="text-center">
              Loading product.
              <br />
              Please wait...
            </p>
          </div>
        ) : product ? (
          <>
            {/* Hero */}
            <section
              className="education-hero"
              style={{
                backgroundImage: `url(${
                  "/images/school.jpg"
                })`,
              }}
            >
              <div className="education-hero-overlay">

                <h1>
                  Magyne Campus <br/> University & College
                </h1>

                <p>
                  Magyne Campus helps universities and colleges manage
                  students, admissions, programmes, courses, academics,
                  examinations, staff, fees and campus operations from
                  one integrated platform.
                </p>

                {!isActive && (
                  <div className="product-status-message">
                    {getStatusMessage()}
                  </div>
                )}

                <button
                  className="cta-btn"
                  onClick={handleNavigate}
                  disabled={!isActive}
                  title={
                    !isActive
                      ? getStatusMessage()
                      : "Choose a Magyne Campus package"
                  }
                >
                  {getButtonText()}
                </button>

              </div>
            </section>

            {/* Features */}
            <section className="healthcare-features">

              <h2>Key Features</h2>

              <div className="features-grid">

                <div className="feature-card">
                  <h3>Student Management</h3>

                  <p className="details">
                    Manage student profiles, registrations, academic
                    histories, programmes and enrolment information
                    throughout the student lifecycle.
                  </p>

                  <p className="benefits">
                    Benefits: Centralized student records, reduced
                    administrative work and better access to information.
                  </p>
                </div>

                <div className="feature-card">
                  <h3>Admissions & Enrollment</h3>

                  <p className="details">
                    Manage applications, admissions, registrations and
                    student enrollment across programmes and academic
                    periods.
                  </p>

                  <p className="benefits">
                    Benefits: Streamlined admissions, fewer errors and
                    faster student onboarding.
                  </p>
                </div>

                <div className="feature-card">
                  <h3>Academic Management</h3>

                  <p className="details">
                    Manage faculties, departments, programmes, courses,
                    classes and academic structures from one system.
                  </p>

                  <p className="benefits">
                    Benefits: Better academic organization and centralized
                    management of institutional programmes.
                  </p>
                </div>

                <div className="feature-card">
                  <h3>Examinations & Results</h3>

                  <p className="details">
                    Manage examinations, assessments, grades, academic
                    results and student performance records.
                  </p>

                  <p className="benefits">
                    Benefits: Accurate academic records, efficient
                    examination administration and faster results processing.
                  </p>
                </div>

                <div className="feature-card">
                  <h3>Fees & Finance</h3>

                  <p className="details">
                    Manage student fees, invoices, payments, balances
                    and institutional financial transactions.
                  </p>

                  <p className="benefits">
                    Benefits: Improved financial visibility, accurate
                    student balances and easier fee collection.
                  </p>
                </div>

                <div className="feature-card">
                  <h3>Staff Management</h3>

                  <p className="details">
                    Maintain staff records and organize academic and
                    administrative personnel across the institution.
                  </p>

                  <p className="benefits">
                    Benefits: Better staff administration and improved
                    institutional accountability.
                  </p>
                </div>

              </div>
            </section>

            {/* CTA */}
            <section className="education-cta">

              <h2>
                Transform Your University or College
              </h2>

              <p>
                Empower your institution with Magyne Campus and
                simplify student administration, academics, finance
                and campus operations through one modern platform.
              </p>

              <button
                className="cta-btn"
                onClick={handleNavigate}
                disabled={!isActive}
              >
                {getButtonText()}
              </button>

            </section>
          </>
        ) : (
          /* Invalid Product */
          <div className="login-card mt-lg-5 mb-lg-5">

            <div className="logo-container">

              <h3 className="page-title mb-2">
                Invalid product.
              </h3>

              <p>
                The requested product could not be found.
                Please select a valid Magyne solution.
              </p>

              <p className="register-text">
                Explore our products{" "}
                <a href="/products">
                  See products
                </a>
              </p>

            </div>

          </div>
        )}

      </div>
    </>
  );
}
